<template>
  <BasePage title="Vehicles" subtitle="Manage vehicles information">
    <template #page-header:actions>
      <v-btn
        color="primary"
        depressed
        v-if="currentUser?.hasPermission('fleet.add_vehicle')"
        @click="openVehicleFormDialog()"
      >
        <template #prepend>
          <v-icon start class="hidden-sm-and-down me-0"> {{ route.meta.icon }} </v-icon>
        </template>
        Create Vehicle
      </v-btn>
    </template>

    <template #page-header:bottom>
      <ErrorAlert
        v-if="error != null"
        dense
        :error="error"
        @clearErrors="clearErrors"
        class="mb-0 mt-4 rounded"
      />
    </template>

    <v-card flat height="calc(100vh - 286px)" class="px-4 rounded-lg">
      <v-toolbar flat height="84" color="transparent">
        <v-text-field
          label="Search"
          single-line
          hide-details="auto"
          variant="outlined"
          density="comfortable"
          style="max-width: 400px"
          v-model="search"
          prepend-inner-icon="mdi-magnify"
        />
      </v-toolbar>

      <VehicleList
        :loading="isLoading"
        :vehicles="vehicles ?? []"
        :search="search"
        @vehicle-edit-pressed="openVehicleFormDialog"
        @vehicle-delete-pressed="openDeleteVehicleDialog"
      />
    </v-card>

    <ConfirmationDialog
      v-if="selectedVehicle"
      width="500"
      title="Delete vehicle"
      v-model="vehicleDeleteDialog"
      v-model:error="deleteError"
    >
      <template #message>
        Are you sure you want to delete this vehicle?
        <br />
        <div class="pt-2">This action cannot be undone.</div>
      </template>

      <template #actions>
        <v-spacer></v-spacer>
        <v-btn
          color="error"
          variant="flat"
          :disabled="deleteInProgress"
          :loading="deleteInProgress"
          @click="removeVehicle(selectedVehicle!)"
        >
          Delete
        </v-btn>
      </template>
    </ConfirmationDialog>
  </BasePage>

  <VehicleFormDialog
    v-model:dialog="isVehicleFormDialogActive"
    :vehicle="selectedVehicle!"
    :isEdit="!!selectedVehicle"
    ref="vehicleFormDialogRef"
  />
</template>

<script lang="ts" setup>
import { ref } from 'vue'
import { useAuthStore } from '@/stores'
import { storeToRefs } from 'pinia'
import { useRoute } from 'vue-router'

import type { IVehicle } from '@/models/fleet-management'
import { useDeleteVehicle, useFetchVehicles } from '@/composables/vehicle'

import BasePage from '@/components/base/BasePage.vue'
import ConfirmationDialog from '@/components/common/ConfirmationDialog.vue'
import VehicleFormDialog from '@/components/fleet-management/vehicles/VehicleFormDialog.vue'
import VehicleList from '@/components/fleet-management/vehicles/VehicleList.vue'
import ErrorAlert from '@/components/common/ErrorAlert.vue'

const route = useRoute()

const search = ref<string>('')

const selectedVehicle = ref<IVehicle>()

const isVehicleFormDialogActive = ref(false)

const authStore = useAuthStore()
const { getUser: currentUser } = storeToRefs(authStore)

function openVehicleFormDialog(vehicle?: IVehicle) {
  selectedVehicle.value = vehicle

  isVehicleFormDialogActive.value = true
}

const vehicleDeleteDialog = ref(false)

function openDeleteVehicleDialog(vehicle: IVehicle) {
  selectedVehicle.value = vehicle
  vehicleDeleteDialog.value = true
}

function closeDrawerAndDialog() {
  vehicleDeleteDialog.value = false
}

// CORE
const { vehicles, isLoading, error } = useFetchVehicles()

function clearErrors() {
  error.value = null
}
const {
  isPending: deleteInProgress,
  error: deleteError,
  mutate: deleteVehicle
} = useDeleteVehicle()

function removeVehicle(vehicle: IVehicle) {
  deleteVehicle(vehicle.id!, {
    onSuccess: () => {
      closeDrawerAndDialog()
    }
  })
}
</script>

<style lang="scss">
.site-list .v-list-item--three-line .v-list-item-subtitle {
  -webkit-line-clamp: 5;
}

.card-list {
  --v-card-list-gap: 0.25rem;

  &.v-list {
    padding-block: 0;
  }

  .v-list-item {
    min-block-size: unset;
    min-block-size: auto !important;
    padding-block: 0 !important;
    padding-inline: 0 !important;

    > .v-ripple__container {
      opacity: 0;
    }

    &:not(:last-child) {
      padding-block-end: var(--v-card-list-gap) !important;
    }
  }

  .v-list-item:hover,
  .v-list-item:focus,
  .v-list-item:active,
  .v-list-item.active {
    > .v-list-item__overlay {
      opacity: 0 !important;
    }
  }
}
</style>
